// src/components/FAQ.tsx
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const FAQItemBlock: React.FC<{
  item: FAQItem;
  open: boolean;
  onToggle: () => void;
}> = ({ item, open, onToggle }) => {
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-6 py-4 bg-sub hover:bg-accent flex justify-between items-center rounded-2xl transition-colors group"
      >
        <span className="font-semibold text-main group-hover:text-white transition-colors">
          {item.question}
        </span>
        <span className="text-accent font-bold text-xl group-hover:text-white transition-colors">
          {open ? "−" : "+"}
        </span>
      </button>

      {open && (
        <div className="px-6 py-4 text-gray-700 bg-white leading-relaxed">
          {item.answer}
        </div>
      )}
    </div>
  );
};

const FAQ: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === "ko";
  const section = t("faq", { returnObjects: true }) as {
    title: string;
    categories: FAQCategory[];
  };

  const [open, setOpen] = useState<{
    category: number | null;
    item: number | null;
  }>({ category: null, item: null });

  const toggle = (catIndex: number, itemIndex: number) => {
    setOpen((prev) =>
      prev.category === catIndex && prev.item === itemIndex
        ? { category: null, item: null }
        : { category: catIndex, item: itemIndex }
    );
  };

  return (
    <section className="max-w-6xl mx-auto py-20 px-4">
      <Helmet>
        <title>
          {isKo
            ? "자주 묻는 질문 — 영국 취업 컨설팅 | BeMe"
            : "FAQ — UK Job Consulting | BeMe"}
        </title>
        <meta
          name="description"
          content={
            isKo
              ? "BeMe 영국 취업 컨설팅 서비스에 대한 자주 묻는 질문. 가격, 진행 방식, 영국 취업 전문성, 환불 정책까지 — 영국 CV 진단·커버레터·LinkedIn 서비스의 모든 것."
              : "Frequently asked questions about BeMe UK job consulting. Pricing, process, UK hiring expertise, and refund policy for CV, Cover Letter, and LinkedIn services."
          }
        />
      </Helmet>
      {/* Title */}
      <h2 className="text-3xl md:text-4xl font-bold text-main text-center mb-14">
        {section.title}
      </h2>

      {/* Categories */}
      <div className="space-y-14">
        {section.categories.map((category, catIndex) => (
          <div key={catIndex}>
            <h3 className="text-2xl font-bold text-main mb-6">
              {category.title}
            </h3>

            <div className="space-y-4">
              {category.items.map((item, itemIndex) => (
                <FAQItemBlock
                  key={itemIndex}
                  item={item}
                  open={
                    open.category === catIndex &&
                    open.item === itemIndex
                  }
                  onToggle={() => toggle(catIndex, itemIndex)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FAQ;
